import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Button, 
  IconButton, 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableRow, 
  TextField, 
  Select, 
  MenuItem,
  useTheme,
  Grid,
  CircularProgress,
  Alert,
  TablePagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
  TableContainer
} from '@mui/material';

import axios from 'axios';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { 
  Add as AddIcon, 
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon} from '@mui/icons-material';

const AgendaProcedimientos = () => {
  const theme = useTheme();
  const [agenda, setAgenda] = useState([]);
  const [nombrePaciente, setNombrePaciente] = useState('');
  const [medico, setMedico] = useState('');
  const [tipoProcedimiento, setTipoProcedimiento] = useState('');
  const [horaCita, setHoraCita] = useState('09:00');
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editingId, setEditingId] = useState(null);
  const [editNombre, setEditNombre] = useState('');
  const [editMedico, setEditMedico] = useState('');
  const [editTipoProcedimiento, setEditTipoProcedimiento] = useState('');
  const [editHoraCita, setEditHoraCita] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL;

  const fetchAgenda = useCallback(async () => {
    try {
      setLoading(true);
      const fecha = selectedDate.format('YYYY-MM-DD');
      const response = await axios.get(`${API_URL}/api/agenda-procedimientos`, {
        params: { fecha }
      });
      
      const validStates = ['Agendado', 'En Espera', 'En Proceso', 'Finalizado', 'Cancelado'];
      const sortedAgenda = response.data.map(item => ({
        ...item,
        estado: validStates.includes(item.estado) ? item.estado : 'Agendado'
      })).sort((a, b) => new Date(b.hora_cita) - new Date(a.hora_cita));

      console.log('Datos recibidos de la API:', sortedAgenda);
      setAgenda(sortedAgenda);
      setError('');
    } catch (err) {
      console.error('Error fetching agenda de procedimientos:', err);
      setError('Error al cargar la agenda de procedimientos. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  }, [selectedDate, API_URL]);

  useEffect(() => {
    fetchAgenda();
  }, [fetchAgenda]);

  const handleAddProcedimiento = async () => {
    if (!medico) {
      setError('Médico es requerido');
      return;
    }

    if (!nombrePaciente) {
      setError('Nombre del paciente es requerido');
      return;
    }

    if (!tipoProcedimiento) {
      setError('Tipo de procedimiento es requerido');
      return;
    }

    try {
      const fecha = selectedDate.format('YYYY-MM-DD');
      const response = await axios.post(`${API_URL}/api/agenda-procedimientos`, {
        nombre_paciente: nombrePaciente,
        medico,
        tipo_procedimiento: tipoProcedimiento,
        hora_cita: horaCita,
        fecha,
        estado: 'Agendado'
      });
      
      setAgenda(prevAgenda => [response.data, ...prevAgenda]);
      setNombrePaciente('');
      setMedico('');
      setTipoProcedimiento('');
      setHoraCita('09:00');
    } catch (err) {
      console.error('Error adding procedimiento:', err);
      setError('Error al agregar el procedimiento. Por favor, intente nuevamente.');
    }
  };

  const handleEditSave = async (id) => {
    try {
      setAgenda(prevAgenda => 
        prevAgenda.map(item => 
          item.id === id ? { 
            ...item, 
            nombre_paciente: editNombre,
            medico: editMedico,
            tipo_procedimiento: editTipoProcedimiento,
            hora_cita: editHoraCita
          } : item
        )
      );
      
      await axios.put(`${API_URL}/api/agenda-procedimientos/${id}`, {
        nombre_paciente: editNombre,
        medico: editMedico,
        tipo_procedimiento: editTipoProcedimiento,
        hora_cita: editHoraCita
      });
      
      setEditingId(null);
    } catch (err) {
      fetchAgenda();
      console.error('Error updating procedimiento:', err);
      setError('Error al actualizar el procedimiento. Por favor, intente nuevamente.');
    }
  };

  const handleDelete = async (id) => {
    try {
      setAgenda(prevAgenda => prevAgenda.filter(item => item.id !== id));
      
      await axios.delete(`${API_URL}/api/agenda-procedimientos/${id}`);
      setDeleteDialogOpen(false);
    } catch (err) {
      fetchAgenda();
      console.error('Error deleting procedimiento:', err);
      setError('Error al eliminar el procedimiento. Por favor, intente nuevamente.');
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      setAgenda(prevAgenda => 
        prevAgenda.map(item => 
          item.id === id ? { ...item, estado: newStatus } : item
        )
      );

      await axios.put(`${API_URL}/api/agenda-procedimientos/${id}/estado`, {
        estado: newStatus
      });
    } catch (err) {
      fetchAgenda();
      console.error('Error actualizando estado:', err);
      setError('Error al actualizar el estado. Por favor, intente nuevamente.');
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const currentItems = agenda.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const getEstadoColor = (estado) => {
    switch(estado) {
      case 'Agendado':
        return theme.palette.warning.main;
      case 'En Espera':
        return theme.palette.info.main;
      case 'En Proceso':
        return theme.palette.primary.main;
      case 'Finalizado':
        return theme.palette.success.main;
      case 'Cancelado':
        return '#8e24aa';
      default:
        return 'inherit';
    }
  };

  return (
    <Box sx={{ 
      display: 'flex',
      padding: '64px 16px 16px 16px',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      minHeight: 'calc(100vh - 64px)',
      flexDirection: 'column',
      width: '100%',
      maxWidth: '100%',
      overflowX: 'hidden',
      position: 'relative',
      boxSizing: 'border-box',
      margin: 0
    }}>
      <Paper
        elevation={3}
        sx={{
          padding: { xs: 2, sm: 3 },
          width: '100%',
          maxWidth: '100%',
          overflowX: 'auto',
          borderRadius: 2,
          margin: '0',
          boxSizing: 'border-box',
          background: `linear-gradient(135deg, ${theme.palette.background.default} 0%,rgb(238, 245, 178) 100%)`,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          height: 'auto',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          marginTop: '450px',
          overflow: 'visible'
        }}
      >
        <Box sx={{ 
          display: 'flex',
          padding: '16px',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          minHeight: 'auto',
          flexDirection: 'column',
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
          position: 'relative',
          boxSizing: 'border-box',
          margin: 0
        }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h5" sx={{ 
                fontWeight: 'bold', 
                color: theme.palette.primary.main,
                mb: { xs: 2, sm: 0 }
              }}>
                Agenda de Procedimientos
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Seleccionar Fecha"
                    value={selectedDate}
                    onChange={(newValue) => setSelectedDate(newValue)}
                    format="DD/MM/YYYY"
                    sx={{ flex: 1 }}
                  />
                </LocalizationProvider>
                <Button 
                  variant="contained" 
                  startIcon={<RefreshIcon />} 
                  onClick={fetchAgenda}
                  sx={{ minWidth: 120, height: '56px', ml: 1 }}
                >
                  Actualizar
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Formulario para agregar procedimiento */}
        <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f5f5f5' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                label="Nombre del Paciente"
                fullWidth
                value={nombrePaciente}
                onChange={(e) => setNombrePaciente(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={2.5}>
              <Select
                label="Médico"
                fullWidth
                value={medico}
                onChange={(e) => setMedico(e.target.value)}
                displayEmpty
              >
                <MenuItem value="" disabled>Seleccione médico</MenuItem>
                <MenuItem value="Dr. Carlos Espinoza">Dr. Carlos Espinoza</MenuItem>
                <MenuItem value="Dra. Maria Martha Espinoza">Dra. Maria Martha Espinoza</MenuItem>
                <MenuItem value="Dra. Samanta Espinoza">Dra. Samantha Espinoza</MenuItem>
                <MenuItem value="Pendiente">Pendiente</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={12} md={2.5}>
              <Select
                label="Tipo de Procedimiento"
                fullWidth
                value={tipoProcedimiento}
                onChange={(e) => setTipoProcedimiento(e.target.value)}
                displayEmpty
              >
                <MenuItem value="" disabled>Seleccione tipo</MenuItem>
                <MenuItem value="Densitometría">Densitometría</MenuItem>
                <MenuItem value="Cirugía">Cirugía</MenuItem>
                <MenuItem value="Otros">Otros</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                label="Hora de la Cita"
                type="time"
                fullWidth
                value={horaCita}
                onChange={(e) => setHoraCita(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button 
                variant="contained" 
                startIcon={<AddIcon />}
                onClick={handleAddProcedimiento}
                fullWidth
                sx={{ height: '56px' }}
              >
                Agregar
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: theme.palette.primary.light, color: 'white' }}>Nombre del Paciente</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: theme.palette.primary.light, color: 'white' }}>Hora de la Cita</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: theme.palette.primary.light, color: 'white' }}>Médico</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: theme.palette.primary.light, color: 'white' }}>Tipo de Procedimiento</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: theme.palette.primary.light, color: 'white' }}>Estado</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: theme.palette.primary.light, color: 'white' }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : currentItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    No hay procedimientos registrados para esta fecha.
                  </TableCell>
                </TableRow>
              ) : (
                currentItems.map((row) => (
                  <TableRow key={row.id}>
                    {editingId === row.id ? (
                      // Modo edición
                      <>
                        <TableCell>
                          <TextField
                            value={editNombre}
                            onChange={(e) => setEditNombre(e.target.value)}
                            fullWidth
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="time"
                            value={editHoraCita}
                            onChange={(e) => setEditHoraCita(e.target.value)}
                            fullWidth
                            size="small"
                            InputLabelProps={{
                              shrink: true,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Select
                            value={editMedico}
                            onChange={(e) => setEditMedico(e.target.value)}
                            fullWidth
                            size="small"
                          >
                            <MenuItem value="Dr. Carlos Espinoza">Dr. Carlos Espinoza</MenuItem>
                            <MenuItem value="Dra. Maria Martha Espinoza">Dra. Maria Martha Espinoza</MenuItem>
                            <MenuItem value="Dra. Samanta Espinoza">Dra. Samantha Espinoza</MenuItem>
                            <MenuItem value="Pendiente">Pendiente</MenuItem>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Select
                            value={editTipoProcedimiento}
                            onChange={(e) => setEditTipoProcedimiento(e.target.value)}
                            fullWidth
                            size="small"
                          >
                            <MenuItem value="Densitometría">Densitometría</MenuItem>
                            <MenuItem value="Cirugía">Cirugía</MenuItem>
                            <MenuItem value="Otros">Otros</MenuItem>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: getEstadoColor(row.estado),
                              fontWeight: 'bold'
                            }}
                          >
                            {row.estado}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Button 
                            variant="contained" 
                            color="primary" 
                            size="small"
                            onClick={() => handleEditSave(row.id)}
                            sx={{ mr: 1 }}
                          >
                            Guardar
                          </Button>
                          <Button 
                            variant="outlined" 
                            color="secondary" 
                            size="small"
                            onClick={() => setEditingId(null)}
                          >
                            Cancelar
                          </Button>
                        </TableCell>
                      </>
                    ) : (
                      // Modo visualización
                      <>
                        <TableCell>{row.nombre_paciente}</TableCell>
                        <TableCell>
                          {row.hora_cita ? row.hora_cita.substring(0, 5) : '-'}
                        </TableCell>
                        <TableCell>{row.medico}</TableCell>
                        <TableCell>{row.tipo_procedimiento}</TableCell>
                        <TableCell>
                          <Select
                            value={row.estado}
                            onChange={(e) => handleStatusChange(row.id, e.target.value)}
                            size="small"
                            sx={{
                              color: getEstadoColor(row.estado),
                              fontWeight: 'bold',
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: getEstadoColor(row.estado)
                              }
                            }}
                          >
                            <MenuItem value="Agendado">Agendado</MenuItem>
                            <MenuItem value="En Espera">En Espera</MenuItem>
                            <MenuItem value="En Proceso">En Proceso</MenuItem>
                            <MenuItem value="Finalizado">Finalizado</MenuItem>
                            <MenuItem value="Cancelado">Cancelado</MenuItem>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <IconButton 
                            color="primary" 
                            onClick={() => {
                              setEditingId(row.id);
                              setEditNombre(row.nombre_paciente);
                              setEditMedico(row.medico);
                              setEditTipoProcedimiento(row.tipo_procedimiento);
                              setEditHoraCita(row.hora_cita);
                            }}
                            title="Editar"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton 
                            color="error" 
                            onClick={() => {
                              setDeleteId(row.id);
                              setDeleteDialogOpen(true);
                            }}
                            title="Eliminar"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={agenda.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Filas por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </TableContainer>
      </Paper>

      {/* Diálogo de confirmación para eliminar */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro de que desea eliminar este procedimiento?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            Cancelar
          </Button>
          <Button 
            onClick={() => handleDelete(deleteId)} 
            color="error"
            variant="contained"
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgendaProcedimientos;
